import type { SceneBuilder } from "../../reel/scenes.js";
import {
  clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml, gsapSetLine,
} from "../../reel/sceneShared.js";

/**
 * Hyperframes port of `src/scenes/text/flashStop.tsx`. Single STOP word with
 * a quick scale-in + shake + white flash overlay.
 */
export const flashStop: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const word = (inputs.text ?? "STOPP").toUpperCase();
  const sub = inputs.subtext ?? "";
  const mega = typeSize("mega", ctx.format);
  const lg = typeSize("lg", ctx.format);

  const t = makeTl(clipStartSec);
  const wordId = `${clipPrefix}-w`;
  const subId = `${clipPrefix}-sub`;
  const flashId = `${clipPrefix}-flash`;
  const radialId = `${clipPrefix}-radial`;

  const inner = `<div id="${radialId}" style="position:absolute;inset:0;background:radial-gradient(circle at 50% 45%, ${withAlpha(themeColors.primary, 0.7)}, transparent 60%);"></div>
<h1 id="${wordId}" style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${mega}px;line-height:.95;letter-spacing:-3.5px;color:${themeColors.fg};text-align:center;text-shadow:0 0 80px ${withAlpha(themeColors.fg, 0.45)};">${escapeHtml(word)}</h1>
${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${lg * 0.55}px;color:${withAlpha(themeColors.fg, 0.92)};text-align:center;max-width:${ctx.format === "9:16" ? 820 : 1200}px;letter-spacing:-0.4px;">${escapeHtml(sub)}</div>` : ""}
<div id="${flashId}" style="position:absolute;inset:0;background:${themeColors.fg};mix-blend-mode:screen;pointer-events:none;"></div>`;

  const html = clipFrame({ clipPrefix, clipStartSec, clipDurationSec, inner, gapPx: 40 });

  const init = [
    gsapSetLine(`#${wordId}`, { scale: 1.4, opacity: 0 }),
    gsapSetLine(`#${flashId}`, { opacity: 1 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 18 }) : "",
  ].filter(Boolean).join("\n");

  const tweens = [
    t.to(`#${wordId}`, { scale: 1, opacity: 1, duration: 0.4, ease: "raw:\"back.out(1.6)\"" }, 0),
    // Shake first ~14 frames via 3 keyframes
    t.to(`#${wordId}`, { x: 6, duration: 0.05, ease: "raw:\"sine.inOut\"", yoyo: true, repeat: 5 }, 0),
    t.to(`#${flashId}`, { opacity: 0.4, duration: 0.1, ease: "raw:\"power2.out\"" }, 0.1),
    t.to(`#${flashId}`, { opacity: 0, duration: 0.16, ease: "raw:\"power1.in\"" }, 0.2),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.47, ease: "raw:\"power1.out\"" }, 14 / 30));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8));

  return combineSceneFragments([{ html, init, tweens }]);
};
